import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Users, 
  Package, 
  Save,
  RefreshCw,
  Check,
  User,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PenAssignments = () => {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [penAssignments, setPenAssignments] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [staffPenSelections, setStaffPenSelections] = useState({});

  // Pens array: 1-6 regular pens, 7 is Brooding Pen, 8 is Grower Pen
  const pens = [1, 2, 3, 4, 5, 6, 7, 8];
  const getPenLabel = (pen) => {
    if (pen === 7) return 'Brooding Pen';
    if (pen === 8) return 'Grower Pen';
    return `Pen ${pen}`;
  };

  useEffect(() => {
    fetchPenAssignments();
  }, []);

  const fetchPenAssignments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/pen-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPenAssignments(data.pen_assignments || {});
        setStaffList(data.staff_list || []);
        
        // Initialize selections from current assignments
        const selections = {};
        data.staff_list.forEach(staff => {
          selections[staff.id] = staff.assigned_pens || [];
        });
        setStaffPenSelections(selections);
      }
    } catch (error) {
      console.error('Error fetching pen assignments:', error);
      toast.error('Failed to load pen assignments');
    } finally {
      setLoading(false);
    }
  };

  const handlePenToggle = (staffId, penNumber) => {
    setStaffPenSelections(prev => {
      const current = prev[staffId] || [];
      if (current.includes(penNumber)) {
        return { ...prev, [staffId]: current.filter(p => p !== penNumber) };
      } else {
        return { ...prev, [staffId]: [...current, penNumber].sort((a, b) => a - b) };
      }
    });
  };

  const handleSaveStaffPens = async (staffId) => {
    setSaving(prev => ({ ...prev, [staffId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/users/${staffId}/pen-assignment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ assigned_pens: staffPenSelections[staffId] || [] })
      });

      if (response.ok) {
        toast.success('Pen assignment saved successfully');
        fetchPenAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save pen assignment');
      }
    } catch (error) {
      console.error('Error saving pen assignment:', error);
      toast.error('Failed to save pen assignment');
    } finally {
      setSaving(prev => ({ ...prev, [staffId]: false }));
    }
  };

  const getPenStaffCount = (penNumber) => {
    return penAssignments[penNumber]?.length || 0;
  };

  const isStaffAssignedToPen = (staffId, penNumber) => {
    return (staffPenSelections[staffId] || []).includes(penNumber);
  };

  return (
    <div className="space-y-6" data-testid="pen-assignments-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pen Assignments
          </h1>
          <p className="text-lg text-slate-600 mt-2">Assign staff members to specific pens for production recording</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchPenAssignments}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Pen Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {pens.map(pen => {
          const staffCount = getPenStaffCount(pen);
          const assignedStaff = penAssignments[pen] || [];
          
          return (
            <div
              key={pen}
              className={`rounded-xl border-2 p-4 ${
                staffCount > 0 
                  ? 'bg-emerald-50 border-emerald-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-lg">{getPenLabel(pen)}</span>
                <Package className={`w-5 h-5 ${staffCount > 0 ? 'text-emerald-500' : 'text-amber-500'}`} />
              </div>
              <div className={`text-sm ${staffCount > 0 ? 'text-emerald-700' : 'text-amber-700'}`}>
                {staffCount === 0 ? (
                  <span className="italic">Unassigned</span>
                ) : (
                  <div className="space-y-1">
                    {assignedStaff.map(staff => (
                      <div key={staff.id} className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="text-xs truncate">{staff.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Staff Assignment Table */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-500" />
          Staff Pen Assignments
        </h2>
        
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="pen-assignments-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-3 px-4">Staff Member</th>
                <th className="text-left py-3 px-2">Role</th>
                {pens.map(pen => (
                  <th key={pen} className="text-center py-3 px-2 w-20">
                    {pen === 7 ? 'Brood' : pen === 8 ? 'Grower' : `Pen ${pen}`}
                  </th>
                ))}
                <th className="text-center py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staffList.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-slate-500">
                    No staff members found
                  </td>
                </tr>
              ) : (
                staffList.map(staff => {
                  const hasChanges = JSON.stringify((staff.assigned_pens || []).sort()) !== 
                                     JSON.stringify((staffPenSelections[staff.id] || []).sort());
                  
                  return (
                    <tr key={staff.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-emerald-600" />
                          </div>
                          <span className="font-medium">{staff.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          staff.role === 'manager' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {staff.role}
                        </span>
                      </td>
                      {pens.map(pen => (
                        <td key={pen} className="text-center py-3 px-2">
                          <div className="flex justify-center">
                            <Checkbox
                              checked={isStaffAssignedToPen(staff.id, pen)}
                              onCheckedChange={() => handlePenToggle(staff.id, pen)}
                              data-testid={`pen-checkbox-${staff.id}-${pen}`}
                            />
                          </div>
                        </td>
                      ))}
                      <td className="text-center py-3 px-4">
                        <Button
                          size="sm"
                          onClick={() => handleSaveStaffPens(staff.id)}
                          disabled={saving[staff.id] || !hasChanges}
                          className={hasChanges ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                          data-testid={`save-pen-assignment-${staff.id}`}
                        >
                          {saving[staff.id] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : hasChanges ? (
                            <>
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Saved
                            </>
                          )}
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Panel */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-800">How Pen Assignments Work</h3>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>Staff can only record production for their assigned pens</li>
              <li>Unassigned staff (no pens checked) can access all pens</li>
              <li>Multiple staff can be assigned to the same pen</li>
              <li>One staff can be assigned to multiple pens</li>
              <li>Admins always have access to all pens regardless of assignment</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PenAssignments;
